import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressSeatingSeatingReservationServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    _api_base_domain_: [
      {
        srcPath: '/seating-reservations',
        destPath: '',
      },
    ],
    'api._base_domain_': [
      {
        srcPath: '/seating-reservations',
        destPath: '',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/seating-reservations',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/seating-reservations',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/seating-reservations',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/seating-reservations',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/seating-reservations',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/seating-reservations',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/seating-reservations',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/seating/v1/reservations',
        destPath: '/v1/reservations',
      },
    ],
    _: [
      {
        srcPath: '/_api/seating-reservations',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_seatings_seating-reservation';

/** Creates a seating reservation for one or more places within a seating plan. */
export function createSeatingReservation(
  payload: object
): RequestOptionsFactory<any> {
  function __createSeatingReservation({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'reservation.createdDate' },
          { path: 'reservation.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.seating.v1.seating_reservation',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.seating.SeatingReservationService.CreateSeatingReservation',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressSeatingSeatingReservationServiceUrl({
        protoPath: '/v1/reservations',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'reservation.createdDate' },
              { path: 'reservation.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createSeatingReservation;
}

/** Retrieves a seating reservation by ID. */
export function getSeatingReservation(
  payload: object
): RequestOptionsFactory<any> {
  function __getSeatingReservation({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.seating.v1.seating_reservation',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.seating.SeatingReservationService.GetSeatingReservation',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressSeatingSeatingReservationServiceUrl({
        protoPath: '/v1/reservations/{reservationId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'reservation.createdDate' },
              { path: 'reservation.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getSeatingReservation;
}

/** Retrieves seating reservations that match specified criteria. */
export function querySeatingReservations(
  payload: object
): RequestOptionsFactory<any> {
  function __querySeatingReservations({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'query.sort.origin.latitude' },
          { path: 'query.sort.origin.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.seating.v1.seating_reservation',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.seating.SeatingReservationService.QuerySeatingReservations',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressSeatingSeatingReservationServiceUrl({
        protoPath: '/v1/reservations/query',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'reservations.createdDate' },
              { path: 'reservations.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __querySeatingReservations;
}

/** Deletes a seating reservation and releases all reserved places. */
export function deleteSeatingReservation(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteSeatingReservation({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.seating.v1.seating_reservation',
      method: 'DELETE' as any,
      methodFqn:
        'com.wixpress.seating.SeatingReservationService.DeleteSeatingReservation',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressSeatingSeatingReservationServiceUrl({
        protoPath: '/v1/reservations/{id}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'reservation.createdDate' },
              { path: 'reservation.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __deleteSeatingReservation;
}

/** Retrieves capacity and reservation summary by seating category. */
export function getSeatingCategorySummaryByExternalId(
  payload: object
): RequestOptionsFactory<any> {
  function __getSeatingCategorySummaryByExternalId({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.seating.v1.seating_reservation',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.seating.SeatingReservationService.GetSeatingCategorySummaryByExternalId',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressSeatingSeatingReservationServiceUrl({
        protoPath: '/v1/reservations/seating-category-summary',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __getSeatingCategorySummaryByExternalId;
}

/** Retrieves detailed place-level occupancy data for a seating plan. */
export function getSeatingReservationSummaryByExternalId(
  payload: object
): RequestOptionsFactory<any> {
  function __getSeatingReservationSummaryByExternalId({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.seating.v1.seating_reservation',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.seating.SeatingReservationService.GetSeatingReservationSummaryByExternalId',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressSeatingSeatingReservationServiceUrl({
        protoPath: '/v1/reservations/seating-reservation-summary/{externalId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'plan.createdDate' }, { path: 'plan.updatedDate' }],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'plan.sections.elements.uiProperties.x' },
              { path: 'plan.sections.elements.uiProperties.y' },
              { path: 'plan.sections.elements.uiProperties.width' },
              { path: 'plan.sections.elements.uiProperties.height' },
              { path: 'plan.sections.elements.uiProperties.rotationAngle' },
              {
                path: 'plan.sections.elements.multiRowProperties.curveAngleTo',
              },
              {
                path: 'plan.sections.elements.multiRowProperties.curveAngleFrom',
              },
              {
                path: 'plan.sections.elements.multiRowProperties.rows.uiProperties.relativeX',
              },
              {
                path: 'plan.sections.elements.multiRowProperties.rows.uiProperties.width',
              },
              {
                path: 'plan.sections.elements.multiRowProperties.rows.uiProperties.curveAngleFrom',
              },
              {
                path: 'plan.sections.elements.multiRowProperties.rows.uiProperties.curveAngleTo',
              },
              { path: 'plan.elementGroups.uiProperties.x' },
              { path: 'plan.elementGroups.uiProperties.y' },
              { path: 'plan.elementGroups.uiProperties.width' },
              { path: 'plan.elementGroups.uiProperties.height' },
              { path: 'plan.elementGroups.uiProperties.rotationAngle' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getSeatingReservationSummaryByExternalId;
}
